import { useState } from 'react';
import { ExternalLink, FileText } from 'lucide-react';
import { LabelWithValue } from '@/components/common/LabelWithValue';
import { PhotoPreviewDialog } from '@/components/common/PhotoPreviewDialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDisplayDateTime } from '@/utils/date';
import { formatFileSize } from '@/utils/number';
import type { PcmpDisbursementProof } from '../../types/pcmpProposal.types';
import { PcmpProposalSection } from './PcmpProposalSection';

const TITLE = 'Disbursement Proof';

interface PcmpDisbursementProofCardProps {
  proof: PcmpDisbursementProof;
}

/** Legacy LDS-763: the Marketing payment proof; an image opens enlarged, a PDF in a new tab. */
export function PcmpDisbursementProofCard({ proof }: PcmpDisbursementProofCardProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { url } = proof;

  return (
    <PcmpProposalSection title={TITLE}>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        {url === null ? (
          <p className="text-sm text-muted-foreground">The proof file is not available.</p>
        ) : proof.isPdf ? (
          <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
            <FileText className="size-8 text-muted-foreground" aria-hidden />
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
            >
              <ExternalLink className="size-3.5" />
              Open PDF
            </a>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-1.5">
            <button
              type="button"
              onClick={() => setIsPreviewOpen(true)}
              title="Click to enlarge"
              className="cursor-zoom-in rounded-lg border p-1 hover:bg-muted"
            >
              <img src={url} alt={TITLE} className="max-h-56 max-w-44 rounded object-contain" />
            </button>
            <span className="text-xs text-muted-foreground">Click the image to enlarge.</span>
            <PhotoPreviewDialog
              open={isPreviewOpen}
              onOpenChange={setIsPreviewOpen}
              photoUrl={url}
              title={TITLE}
            />
          </div>
        )}
        <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-3">
          <LabelWithValue label="Uploaded by">{proof.uploadedBy?.name}</LabelWithValue>
          <LabelWithValue label="Uploaded at">
            {formatDisplayDateTime(proof.uploadedAt)}
          </LabelWithValue>
          <LabelWithValue label="Size">
            {proof.sizeBytes === null ? null : formatFileSize(proof.sizeBytes)}
          </LabelWithValue>
        </div>
      </div>
    </PcmpProposalSection>
  );
}
